import { extentLinear } from '@d3fc/d3fc-extent';
import { VolumeCurveBucketedDataItem } from 'widgets/volumeCurve/data/volumeCurveDataBuckets.ts';
import { AnySeries, seriesSvgMulti } from '@d3fc/d3fc-series';
import gridlines from 'utils/components/TimeSeriesChart/gridlines.ts';
import {
    VolumeCurveChartBucketedDataItem,
    VolumeCurveChartData,
} from 'widgets/volumeCurve/components/chart/chartData.ts';
import { generateGroupedBarSeries } from 'utils/d3fc/chartSeries.ts';
import { generateAreaSeries } from 'utils/components/TimeSeriesChart/area.ts';
import { scaleLinear } from 'd3-scale';
import { VolumeCurveConfiguration } from 'widgets/volumeCurve/store/configuration/VolumeCurveConfiguration.ts';

const Y_TICKS = 5;
export const getYTicks: () => number = () => Y_TICKS;

export const computeYDomain: (
    data: VolumeCurveChartBucketedDataItem[],
    config: VolumeCurveConfiguration
) => [number, number] = (data, config) => {
    const {
        hideHistoricalVolumeExcludingAuction,
        hideTradedVolume,
        hideAuctionVolume,
    } = config;

    return data.length > 0
        ? extentLinear().accessors([
              () => 0,
              (d: VolumeCurveChartBucketedDataItem) =>
                  hideHistoricalVolumeExcludingAuction
                      ? 0
                      : d.historicalVolumeExcludingAuction,
              (d: VolumeCurveChartBucketedDataItem) =>
                  hideTradedVolume ? 0 : d.tradedVolume,
              (d: VolumeCurveChartBucketedDataItem) =>
                  hideAuctionVolume ? 0 : d.historicalAuctionAndTradedVolume,
          ])(data)
        : [0, 1];
};

export const getChartSeries: (
    data: VolumeCurveChartData,
    chartWidth: number,
    historicalCumulativeGradientId: string,
    cumulativeGradientId: string,
    yDomain: [number, number]
) => AnySeries = (
    data,
    chartWidth,
    historicalCumulativeGradientId,
    cumulativeGradientId,
    yDomain
) => {
    const cumulativeScaleFn = getCumulativeScaleFn(data.bucketedData, yDomain);
    const historicalCumulativeTradedSeries = generateAreaSeries(
        (d: VolumeCurveChartBucketedDataItem) => d.cumulativeHistoricalVolume,
        cumulativeScaleFn,
        historicalCumulativeGradientId,
        'cumulativeHistorical'
    );
    const cumulativeTradedSeries = generateAreaSeries(
        (d: VolumeCurveChartBucketedDataItem) => d.cumulativeTradedVolume!,
        cumulativeScaleFn,
        cumulativeGradientId,
        'cumulativeTraded'
    );

    const tradedVolumeBarSeries = generateGroupedBarSeries(
        data.bucketedData.length,
        chartWidth,
        ['volume-historical', 'volume-traded'],
        (d: { barWidthPercentage: number }) => d.barWidthPercentage
    );

    const auctionVolumeBarSeries = generateGroupedBarSeries(
        data.bucketedData.length,
        chartWidth,
        ['volume-auction', 'auction-series-padding'],
        (d: { barWidthPercentage: number }) => d.barWidthPercentage
    );

    return seriesSvgMulti()
        .series([
            gridlines(Y_TICKS),
            historicalCumulativeTradedSeries,
            cumulativeTradedSeries,
            auctionVolumeBarSeries,
            tradedVolumeBarSeries,
        ])
        .mapping(
            (
                dataSeries: VolumeCurveBucketedDataItem[],
                index: number,
                series: AnySeries[]
            ) => {
                switch (series[index]) {
                    case auctionVolumeBarSeries:
                        return data.auctionVolumeBarData;
                    case tradedVolumeBarSeries:
                        return data.tradedVolumeBarData;
                    case cumulativeTradedSeries:
                        return data.bucketedData.filter(
                            (d) => d.cumulativeTradedVolume !== undefined
                        );

                    default:
                        return dataSeries;
                }
            }
        );
};

function getCumulativeScaleFn(
    dataSeries: VolumeCurveChartBucketedDataItem[],
    yAxisDomain: [number, number]
) {
    const cumulativeDomain = extentLinear().accessors([
        () => 0,
        (d: VolumeCurveChartBucketedDataItem) => d.cumulativeTradedVolume!,
        (d: VolumeCurveChartBucketedDataItem) => d.cumulativeHistoricalVolume!,
    ])(dataSeries);

    return scaleLinear().domain(cumulativeDomain).range(yAxisDomain);
}
